/**
 * Account hint storage.
 *
 * Persists ONLY the user's last-known account email to localStorage so the
 * cold-load silent refresh in `App.tsx` can pass it as `login_hint` to GIS.
 * Without a hint, GIS may pop the account chooser even when a single grant
 * exists, defeating the "no UI on refresh" UX goal.
 *
 * Sharp edges:
 *   - Email is NOT a credential. It MUST NOT be combined with the access
 *     token in this same key. The auth session module (`session.ts`) keeps
 *     the access token in memory only; mixing them here would re-introduce
 *     the XSS exfiltration risk this whole change is trying to remove.
 *   - Empty / whitespace-only inputs are rejected silently. Persisting "" or
 *     "   " would make `login_hint` useless and could trip GIS error paths.
 *   - The localStorage key is dedicated (`mochia.account.v1`) and cleared
 *     by `clearAccount` on logout.
 */

const KEY = 'mochia.account.v1';

export function getAccount(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(KEY);
    if (!v || v.trim().length === 0) return null;
    return v;
  } catch {
    return null;
  }
}

export function setAccount(email: string): void {
  if (typeof localStorage === 'undefined') return;
  if (typeof email !== 'string') return;
  const trimmed = email.trim();
  if (trimmed.length === 0) return;
  try {
    localStorage.setItem(KEY, trimmed);
  } catch {
    /* private mode / quota — non-fatal */
  }
}

export function clearAccount(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* non-fatal */
  }
}
